import { SOLVE_V2_BASE_URL } from '../constants';
import { dashboardApi } from '../dashboard-api';
import type {
  CustomEventChart,
  CustomEventChartBase,
  DatasetRequest,
  DatasetResponse,
  ResolutionComparisonLineGraphResponse,
  ResolutionComparisonResponse,
  ResolutionOverviewSummaryResponse,
  ResolutionRateBarGraphResponse,
} from './types';

const insightCardsApi = dashboardApi.injectEndpoints({
  endpoints: builder => ({
    createChart: builder.mutation<CustomEventChart, CustomEventChartBase>({
      invalidatesTags: [{ id: 'LIST', type: 'InsightCards' }],
      query: body => ({
        body,
        method: 'POST',
        url: `${SOLVE_V2_BASE_URL}/custom-events/charts`,
      }),
    }),
    deleteChart: builder.mutation<void, string>({
      invalidatesTags: (result, error, chart_id) => [
        { id: chart_id, type: 'InsightCards' },
        { id: 'LIST', type: 'InsightCards' },
      ],
      query: chart_id => ({
        method: 'DELETE',
        url: `${SOLVE_V2_BASE_URL}/custom-events/charts/${chart_id}`,
      }),
    }),
    duplicateChart: builder.mutation<CustomEventChart, string>({
      invalidatesTags: [{ id: 'LIST', type: 'InsightCards' }],
      query: chart_id => ({
        method: 'POST',
        url: `${SOLVE_V2_BASE_URL}/custom-events/charts/${chart_id}/duplicate`,
      }),
    }),
    fetchDataset: builder.query<DatasetResponse, DatasetRequest>({
      query: body => ({
        body,
        method: 'POST',
        url: `${SOLVE_V2_BASE_URL}/custom-events/dataset`,
      }),
    }),
    getChartById: builder.query<CustomEventChart, string>({
      providesTags: (result, error, id) => [{ id, type: 'InsightCards' }],
      query: chart_id => ({
        url: `${SOLVE_V2_BASE_URL}/custom-events/charts/${chart_id}`,
      }),
    }),
    getCharts: builder.query<CustomEventChart[], void>({
      providesTags: result =>
        result
          ? [
              ...result.map(
                ({ chart_id }) =>
                  ({ id: chart_id, type: 'InsightCards' }) as const,
              ),
              { id: 'LIST', type: 'InsightCards' },
            ]
          : [{ id: 'LIST', type: 'InsightCards' }],
      query: () => ({
        url: `${SOLVE_V2_BASE_URL}/custom-events/charts`,
      }),
    }),
    getCustomEventPropertyValues: builder.query<
      { data: string[] },
      { channels: string[]; end: number; property_id: string; start: number }
    >({
      query: ({ channels, end, property_id, start }) => ({
        params: {
          channels,
          end,
          property_id,
          start,
        },
        url: `${SOLVE_V2_BASE_URL}/custom-events/event-values`,
      }),
    }),
    getResolutionInsightsConversationCharts: builder.query<
      CustomEventChart[],
      void
    >({
      query: () => ({
        url: `${SOLVE_V2_BASE_URL}/resolution-insights/conversations/charts`,
      }),
    }),
    getResolutionInsightsOverviewCharts: builder.query<
      CustomEventChart[],
      void
    >({
      query: () => ({
        url: `${SOLVE_V2_BASE_URL}/resolution-insights/overview/charts`,
      }),
    }),
    getResolutionInsightsOverviewResolutionComparison: builder.query<
      ResolutionComparisonResponse,
      { end: number; start: number }
    >({
      query: params => ({
        params,
        url: `${SOLVE_V2_BASE_URL}/resolution-insights/overview/resolution-comparison`,
      }),
    }),
    getResolutionInsightsOverviewResolutionComparisonLineChart: builder.query<
      ResolutionComparisonLineGraphResponse,
      { end: number; period: string; start: number }
    >({
      query: params => ({
        params,
        url: `${SOLVE_V2_BASE_URL}/resolution-insights/overview/resolution-comparison-line-chart`,
      }),
    }),
    getResolutionInsightsOverviewResolutionRateBarChart: builder.query<
      ResolutionRateBarGraphResponse,
      { end: number; period: string; start: number }
    >({
      query: params => ({
        params,
        url: `${SOLVE_V2_BASE_URL}/resolution-insights/overview/resolution-rate-bar-chart`,
      }),
    }),
    getResolutionInsightsOverviewSummary: builder.query<
      ResolutionOverviewSummaryResponse,
      { end: number; start: number }
    >({
      query: params => ({
        params,
        url: `${SOLVE_V2_BASE_URL}/resolution-insights/overview/summary`,
      }),
    }),
    updateChart: builder.mutation<
      CustomEventChart,
      { chart_id: string; data: CustomEventChartBase }
    >({
      invalidatesTags: (result, error, { chart_id }) => [
        { id: chart_id, type: 'InsightCards' },
        { id: 'LIST', type: 'InsightCards' },
      ],
      query: ({ chart_id, data }) => ({
        body: data,
        method: 'POST',
        url: `${SOLVE_V2_BASE_URL}/custom-events/charts/${chart_id}`,
      }),
    }),
  }),
});

export const {
  useCreateChartMutation,
  useDeleteChartMutation,
  useDuplicateChartMutation,
  useFetchDatasetQuery,
  useGetChartByIdQuery,
  useGetChartsQuery,
  useGetCustomEventPropertyValuesQuery,
  useGetResolutionInsightsConversationChartsQuery,
  useGetResolutionInsightsOverviewChartsQuery,
  useGetResolutionInsightsOverviewResolutionComparisonLineChartQuery,
  useGetResolutionInsightsOverviewResolutionComparisonQuery,
  useGetResolutionInsightsOverviewResolutionRateBarChartQuery,
  useGetResolutionInsightsOverviewSummaryQuery,
  useUpdateChartMutation,
} = insightCardsApi;
